import { getDate, getYear, Locale, set } from 'date-fns';
import arSA from 'date-fns/locale/ar-SA';
import bg from 'date-fns/locale/bg';
import cs from 'date-fns/locale/cs';
import da from 'date-fns/locale/da';
import de from 'date-fns/locale/de';
import el from 'date-fns/locale/el';
import enGB from 'date-fns/locale/en-GB';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import et from 'date-fns/locale/et';
import fi from 'date-fns/locale/fi';
import fr from 'date-fns/locale/fr';
import frCA from 'date-fns/locale/fr-CA';
import he from 'date-fns/locale/he';
import hr from 'date-fns/locale/hr';
import hu from 'date-fns/locale/hu';
import id from 'date-fns/locale/id';
import it from 'date-fns/locale/it';
import ja from 'date-fns/locale/ja';
import ko from 'date-fns/locale/ko';
import lt from 'date-fns/locale/lt';
import lv from 'date-fns/locale/lv';
import ms from 'date-fns/locale/ms';
import nb from 'date-fns/locale/nb';
import nl from 'date-fns/locale/nl';
import pl from 'date-fns/locale/pl';
import ptBR from 'date-fns/locale/pt-BR';
import ro from 'date-fns/locale/ro';
import ru from 'date-fns/locale/ru';
import sk from 'date-fns/locale/sk';
import sl from 'date-fns/locale/sl';
import sr from 'date-fns/locale/sr';
import sv from 'date-fns/locale/sv';
import th from 'date-fns/locale/th';
import tr from 'date-fns/locale/tr';
import uk from 'date-fns/locale/uk';
import vi from 'date-fns/locale/vi';
import zhCN from 'date-fns/locale/zh-CN';
import zhTW from 'date-fns/locale/zh-TW';
import { DateRange, Matcher } from 'react-day-picker';
import type { IntlShape } from 'react-intl';

import { DateValue, View } from '../types';

export const MIN_YEAR = 1;
export const MIN_DATE = set(new Date(), {
  year: MIN_YEAR,
  month: 0,
  date: 1,
});
export const YEAR_PAGE_SIZE = 20;

export const floorYear = (year: number) =>
  Math.floor(year / YEAR_PAGE_SIZE) * YEAR_PAGE_SIZE + 1;

export const getYearRange = (displayDate: Date) => {
  const floor = floorYear(getYear(displayDate));
  return [floor, floor + YEAR_PAGE_SIZE - 1];
};

export const testDisablePreviousYear = (displayDate: Date, view: View) => {
  if (view === 'year') {
    return floorYear(getYear(displayDate)) <= MIN_YEAR;
  }

  return displayDate <= MIN_DATE;
};

export const getDisabledMatcher = ({
  disabled,
  disabledDates = [],
  disabledDays = [],
  max,
  min,
}: {
  disabled?: boolean;
  disabledDates?: Date[];
  disabledDays?: number[];
  max?: Date;
  min?: Date;
}): Matcher | Matcher[] => {
  const matcher = [
    ...disabledDates,
    {
      dayOfWeek: disabledDays,
    },
    (date: Date) => {
      if (disabled || (max && date > max) || (min && date < min)) {
        return true;
      }
      return false;
    },
  ];

  return matcher;
};

export const getMonth = (value: DateValue, isRange?: boolean): Date => {
  const monthValue = isRange ? (value as DateRange)?.from : (value as Date);
  return monthValue || new Date();
};

export const getSelectedDate = (
  dateValue: Matcher | Matcher[] | undefined,
  isRange?: boolean,
): number => {
  const date = isRange ? (dateValue as DateRange)?.from : (dateValue as Date);
  return getDate(date || new Date());
};

export const getNavigateUnit = (view: View, intl: IntlShape) => {
  switch (view) {
    case 'day':
      return intl.formatMessage({
        id: 'DateInput.dayPicker.DayNavigateUnit',
        defaultMessage: 'month',
        description: 'Unit to navigate by month in DayPicker',
      });
    case 'month':
      return intl.formatMessage({
        id: 'DateInput.dayPicker.monthNavigateUnit',
        defaultMessage: 'year',
        description: 'Unit to navigate by year in DayPicker',
      });
    case 'year':
      return intl.formatMessage({
        id: 'DateInput.dayPicker.yearNavigateUnit',
        defaultMessage: 'years',
        description: 'Unit to navigate by years in DayPicker',
      });
  }
};

export const getDateFnsLocale = (
  reactIntlLocale: string | undefined,
): Locale => {
  const dateFnsLocales: Record<string, Locale> = {
    // Language-only codes (alphabetical)
    ar: arSA, // Arabic (generic) → ar-SA
    bg: bg, // Bulgarian → bg
    cs: cs, // Czech → cs
    da: da, // Danish → da
    de: de, // German (generic) → de
    el: el, // Greek → el
    en: enUS, // English (generic) → en-US
    es: es, // Spanish (generic) → es
    et: et, // Estonian → et
    fi: fi, // Finnish → fi
    fr: fr, // French (generic) → fr
    he: he, // Hebrew → he
    hr: hr, // Croatian → hr
    hu: hu, // Hungarian → hu
    id: id, // Indonesian → id
    it: it, // Italian → it
    ja: ja, // Japanese → ja
    ko: ko, // Korean → ko
    lt: lt, // Lithuanian → lt
    lv: lv, // Latvian → lv
    ms: ms, // Malay → ms
    nb: nb, // Norwegian → nb
    nl: nl, // Dutch → nl
    pl: pl, // Polish → pl
    pt: ptBR, // Portuguese → Brazil
    ro: ro, // Romanian → ro
    ru: ru, // Russian → ru
    sk: sk, // Slovak → sk
    sl: sl, // Slovenian → sl
    sr: sr, // Serbian → sr
    sv: sv, // Swedish → sv
    th: th, // Thai → th
    tr: tr, // Turkish → tr
    uk: uk, // Ukrainian → uk
    vi: vi, // Vietnamese → vi
    zh: zhCN, // Chinese → Simplified

    // Language-region codes (alphabetical)
    'ar-EG': arSA, // Arabic (Egypt) - fallback to ar-SA
    'bg-BG': bg, // Bulgarian (Bulgaria)
    'cs-CZ': cs, // Czech (Czech Republic)
    'da-DK': da, // Danish (Denmark)
    'de-AT': de, // German (Austria)
    'de-BE': de, // German (Belgium)
    'de-CH': de, // German (Switzerland)
    'de-DE': de, // German (Germany)
    'de-LU': de, // German (Luxembourg)
    'el-GR': el, // Greek (Greece)
    'en-AU': enUS, // English (Australia) → en-US
    'en-CA': enUS, // English (Canada) → en-US
    'en-GB': enGB, // English (United Kingdom)
    'en-HK': enUS, // English (Hong Kong) → en-US
    'en-IE': enUS, // English (Ireland) → en-US
    'en-IN': enUS, // English (India) → en-US
    'en-NZ': enUS, // English (New Zealand) → en-US
    'en-PH': enUS, // English (Philippines) → en-US
    'en-SG': enUS, // English (Singapore) → en-US
    'en-US': enUS, // English (United States)
    'en-ZA': enUS, // English (South Africa) → en-US
    'es-AR': es, // Spanish (Argentina)
    'es-BO': es, // Spanish (Bolivia)
    'es-CL': es, // Spanish (Chile)
    'es-CO': es, // Spanish (Colombia)
    'es-CR': es, // Spanish (Costa Rica)
    'es-DO': es, // Spanish (Dominican Republic)
    'es-EC': es, // Spanish (Ecuador)
    'es-ES': es, // Spanish (Spain)
    'es-GT': es, // Spanish (Guatemala)
    'es-HN': es, // Spanish (Honduras)
    'es-MX': es, // Spanish (Mexico)
    'es-NI': es, // Spanish (Nicaragua)
    'es-PA': es, // Spanish (Panama)
    'es-PE': es, // Spanish (Peru)
    'es-PR': es, // Spanish (Puerto Rico)
    'es-PY': es, // Spanish (Paraguay)
    'es-SV': es, // Spanish (El Salvador)
    'es-US': es, // Spanish (United States)
    'es-UY': es, // Spanish (Uruguay)
    'es-VE': es, // Spanish (Venezuela)
    'et-EE': et, // Estonian (Estonia)
    'fi-FI': fi, // Finnish (Finland)
    'fr-BE': fr, // French (Belgium)
    'fr-CA': frCA, // French (Canada)
    'fr-CH': fr, // French (Switzerland)
    'fr-FR': fr, // French (France)
    'fr-LU': fr, // French (Luxembourg)
    'he-IL': he, // Hebrew (Israel)
    'hr-HR': hr, // Croatian (Croatia)
    'hu-HU': hu, // Hungarian (Hungary)
    'id-ID': id, // Indonesian (Indonesia)
    'it-IT': it, // Italian (Italy)
    'lt-LT': lt, // Lithuanian (Lithuania)
    'lv-LV': lv, // Latvian (Latvia)
    'ms-MY': ms, // Malay (Malaysia)
    'nb-NO': nb, // Norwegian Bokmal (Norway)
    'nl-NL': nl, // Dutch (Netherlands)
    'pl-PL': pl, // Polish (Poland)
    'pt-BR': ptBR, // Portuguese (Brazil)
    'ro-RO': ro, // Romanian (Romania)
    'ru-RU': ru, // Russian (Russia)
    'sk-SK': sk, // Slovak (Slovakia)
    'sl-SI': sl, // Slovenian (Slovenia)
    'sr-RS': sr, // Serbian Cyrillic (Serbia)
    'sv-SE': sv, // Swedish (Sweden)
    'th-TH': th, // Thai (Thailand)
    'tr-TR': tr, // Turkish (Turkey)
    'uk-UA': uk, // Ukrainian (Ukraine)
    'vi-VN': vi, // Vietnamese (Vietnam)
    'zh-CN': zhCN, // Chinese, Simplified (China)
    'zh-Hans': zhCN, // Chinese (Simplified Script)
    'zh-Hant': zhTW, // Chinese (Traditional Script)
    'zh-TW': zhTW, // Chinese, Traditional (Taiwan)
  };

  // If no locale provided, use browser locale
  const locale = reactIntlLocale
    ? reactIntlLocale
    : (navigator.language ?? 'en');

  if (dateFnsLocales[locale]) {
    return dateFnsLocales[locale];
  }

  // Try language-only match (e.g., 'es' from 'es-AR')
  const languageOnly = locale.split('-')[0];
  if (dateFnsLocales[languageOnly]) {
    return dateFnsLocales[languageOnly];
  }

  // Fall back to US English
  else {
    return dateFnsLocales['en-US'];
  }
};
